import { GcpRequestData, useUploadStore } from '../../../../../../store/uploadStore';
import { Customer, ScheduledFlight, Site } from '../../../../../../types';
import { trackAction } from '../../../../../../libs/analytics/ActionTracking';
import getGcpRequestMeasurementsProcessorService from '../../../../../../api_services/GcpRequestMeasurementsProcessorService';
import { useAppStore } from '../../../../../../store/appStore';
import { useIsSelfServicedPilot } from '../../../../../../hooks/useIsSelfServicedPilot';
import { getTimezoneFromCoordinates } from '../../../../../../libs/getTimezoneFromCoordinates';
import { substrDateFromAirtableFormat } from '../../../helpers/date';
import { formatInTimeZone } from 'date-fns-tz';
import { planGcpServiceDate } from './resolveGcpServiceDate';
import confirmGcpServiceDate from './confirmGcpServiceDate';
import { isGcpServiceDateCancelledError } from './GcpServiceDateCancelledError';

export const useHandleGcpRequestSubmit = (
  customer: Customer,
  site: Site,
  gcpRequest: GcpRequestData,
  schedule?: ScheduledFlight,
) => {
  const { setGcpRequest } = useUploadStore();
  const showWindow = useAppStore((state) => state.showWindow);
  const hideWindow = useAppStore((state) => state.hideWindow);
  const isSelfServiced = useIsSelfServicedPilot() === true;

  return () => {
    (async () => {
      // "Services Performed On" should reflect the day the GCPs were actually measured
      const doneMeasurementUploads = gcpRequest.measurementUploads.filter((u) => u.isDone);
      const timezone = await getTimezoneFromCoordinates(customer.id + '/' + site.id, site.boundary);

      const plan = planGcpServiceDate({
        parseResults: [
          ...(gcpRequest.restoredMeasurementParseResults ?? []),
          ...doneMeasurementUploads.map((u) => u.parseResult),
        ],
        measurementFiles: doneMeasurementUploads.map((u) => u.file),
        isSelfServiced,
        timezone,
        scheduledDate: schedule ? substrDateFromAirtableFormat(schedule.flightDateIso) : undefined,
        nowMs: Date.now(),
      });

      let servicesPerformedOn: string | undefined;
      if (plan.kind === 'modal') {
        try {
          servicesPerformedOn = await confirmGcpServiceDate({
            showWindow,
            hideWindow,
            defaultDate: plan.defaultDate,
            maxDate: formatInTimeZone(new Date(), timezone, 'yyyy-MM-dd'),
            reason: plan.reason,
            siteName: site.name,
          });
        } catch (e) {
          if (isGcpServiceDateCancelledError(e)) return; // pilot backed out — nothing submitted
          throw e;
        }
      } else if (plan.kind === 'send') {
        servicesPerformedOn = plan.date;
      }

      gcpRequest.meta.isSubmitting = true;
      setGcpRequest(customer.id, site.id, gcpRequest);

      trackAction('Send GCP Request', { customer, site });

      await getGcpRequestMeasurementsProcessorService().sendGcpRequest(
        customer.id,
        site.id,
        site.projectInfo.airtableRecordId,
        gcpRequest,
        schedule?.airtableRecordId,
        servicesPerformedOn,
      );

      gcpRequest.meta.prevComment = gcpRequest.comment;
      gcpRequest.meta.prevGcpServiceLaborTimeInHours = gcpRequest.gcpServiceLaborTimeInHours;
      gcpRequest.meta.prevMeasurements = gcpRequest.measurements.concat();
      gcpRequest.meta.prevPhotos = gcpRequest.photos.concat();
      gcpRequest.meta.prevMeasurementUploads = gcpRequest.measurementUploads.concat();
      gcpRequest.meta.prevPhotoUploads = gcpRequest.photoUploads.concat();

      gcpRequest.meta.isEditMode = false;
      gcpRequest.meta.isSubmitting = false;
      setGcpRequest(customer.id, site.id, gcpRequest);
    })();
  };
};
